import { useState, useEffect, useMemo, useRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import {
  Plus,
  AlertTriangle,
  Package,
  CheckCircle,
  CalendarIcon,
  RefreshCw,
  Check,
  AlertCircle as AlertCircleIcon,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";
import { ProductEntry, ProductPayload, CalculationInputs } from "@/types/product/productStep.types";
import {
  validateProductTotals,
  calculateExtractionProductFootprints,
  calculateRatioByQuantity,
  calculateRatioByRevenue,
  getExtractionAllocationType,
} from "../../utils/calculations/extraction";
import { calculateTransformationProductFootprints } from "../../utils/calculations/transformation";
import { ProductEntryModal } from "./product/ProductEntryModal";
import { convertToTonnes } from "@/components/footprints/utils/calculations/unitConversions";
import { useAuth } from "@/contexts/AuthContext";
import { useData } from "@/hooks/useData";
import { Company } from "@/types/company/company.types";
import { MaterialDocWithId } from "@/types/material";
import { StoneType } from "@/types/stone-type/stone-type.types";
import { TransportDistribution } from "@/types/transport-distribution/transport-distribution.types";
import { ProductTable } from "./product/ProductTable";
import { ProductSummary } from "./product/ProductSummary";
import { Stepper } from "@/components/ui/stepper";
import { ConfidentialityDisclaimer } from "@/components/footprints/ConfidentialityDisclaimer";
import MixedActivityProductForm from "@/components/footprints/stepper/forms/product-forms/MixedActivityProductForm";

const calculateProductValidation = (products: any, generalData: any, activityType?: string) => {
  if (!products?.entries?.length || !generalData) {
    return { ...products, isProductionValid: false, isRevenueValid: false };
  }

  const isMixedCompany = activityType === "extraction_and_transformation";
  const isTransformationCompany = activityType === "transformation";
  const isExtractionCompany = activityType === "extraction";

  let isProductionValid = false;
  let isRevenueValid = false;

  if (isMixedCompany) {
    // For mixed companies, validate each activity type separately
    const extractionProducts = products.entries.filter((p: any) => p.productFlow === "extraction");
    const transformationProducts = products.entries.filter((p: any) => p.productFlow === "transformation");

    // Calculate extraction totals
    const extractionValidation = validateProductTotals(extractionProducts, {
      prod_ext: generalData.prod_ext || 0,
      fat_ext: generalData.fat_ext || 0,
    });

    // Calculate transformation totals
    const transformationValidation = validateProductTotals(transformationProducts, {
      prod_ext: generalData.prod_trsf || 0,
      fat_ext: generalData.fat_trsf || 0,
    });

    isProductionValid = extractionValidation.isProductionValid && transformationValidation.isProductionValid;
    isRevenueValid = extractionValidation.isRevenueValid && transformationValidation.isRevenueValid;
  } else {
    // For single activity companies
    let targetData;
    if (isTransformationCompany) {
      targetData = {
        prod_ext: generalData.prod_trsf || 0,
        fat_ext: generalData.fat_trsf || 0,
      };
    } else if (isExtractionCompany) {
      targetData = {
        prod_ext: generalData.prod_ext || 0,
        fat_ext: generalData.fat_ext || 0,
      };
    } else {
      // Fallback
      targetData = {
        prod_ext: generalData.prod_ext || 0,
        fat_ext: generalData.fat_ext || 0,
      };
    }

    const validation = validateProductTotals(products.entries, targetData);
    isProductionValid = validation.isProductionValid;
    isRevenueValid = validation.isRevenueValid;
  }

  return {
    ...products,
    isProductionValid,
    isRevenueValid,
  };
};

interface ProductStepProps {
  year: string;
  existingData?: ProductPayload;
  onDataChange: (payload: ProductPayload) => void;
  onComplete?: () => void;
  onIncomplete?: () => void;
  calculationInputs: CalculationInputs;
  onSaveDraft?: () => void;
  isSavingDraft?: boolean;
  hasUnsavedChanges?: boolean;
  lastSaved?: Date | null;
  formData?: any;
}

// Empty product entry template
const createEmptyEntry = (productFlow: "extraction" | "transformation"): ProductEntry => ({
  id: crypto.randomUUID(),
  productFlow,
  name: "",
  stoneType: "",
  stoneTypeName: "",
  productType: "Bloco",
  functionalUnit: {
    quantity: 1,
    unit: "m3",
  },
  annualProduction: 0,
  productRevenue: 0,
  materialsUsed: [],
  density: 0,

  // Calculated fields (will be computed)
  calculatedProductRevenue: 0,
  ratioByQuantity: 0,
  ratioByRevenue: 0,
  carbonFootprintMaterials: 0,
  carbonFootprintElectricity: 0,
  carbonFootprintElectricityOnly: 0,
  carbonFootprintFuels: 0,
  carbonFootprintResidue: 0,
  carbonFootprintTotal: 0,
  hydricFootprintMaterials: 0,
  hydricFootprintElectricity: 0,
  hydricFootprintWater: 0,
  hydricFootprintTotal: 0,
  carbonFootprintTransport: undefined,
  hydricFootprintTransport: undefined,
  transportType: undefined,
  transports: [],
});

export const ProductStep = ({
  year,
  existingData,
  onDataChange,
  onComplete,
  onIncomplete,
  calculationInputs,
  onSaveDraft,
  isSavingDraft,
  hasUnsavedChanges,
  lastSaved,
  formData,
}: ProductStepProps) => {
  const { t } = useLanguage();
  const { claims } = useAuth();

  // Use ref to store the latest callbacks to avoid dependency issues
  const onDataChangeRef = useRef(onDataChange);
  const onCompleteRef = useRef(onComplete);
  const onIncompleteRef = useRef(onIncomplete);

  useEffect(() => {
    onDataChangeRef.current = onDataChange;
  }, [onDataChange]);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    onIncompleteRef.current = onIncomplete;
  }, [onIncomplete]);

  const { document: company } = useData<Company>({
    collectionName: "companies",
    documentId: claims?.companyId,
  });

  const { data: materialDocs, loading: materialDocsLoading } = useData<MaterialDocWithId>({
    collectionName: "setup_materials",
  });

  const { data: stoneDocs, loading: stoneDocsLoading } = useData<StoneType>({
    collectionName: "setup_stoneTypes",
  });

  const { data: transportDocs } = useData<TransportDistribution>({
    collectionName: "setup_transportDistributions",
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formEntry, setFormEntry] = useState<ProductEntry>(createEmptyEntry("extraction"));
  const [activeSubmenu, setActiveSubmenu] = useState<"extraction" | "transformation">("extraction");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<{ entry: ProductEntry; index: number } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const activityType = company?.activityType;

  useEffect(() => {
    if (materialDocsLoading || stoneDocsLoading) return;
    if (existingData?.entries && existingData.entries.length > 0 && materialDocs && stoneDocs) {
      let hasChanges = false;

      const updatedEntries = existingData.entries.map((entry) => {
        // Update materialsUsed with quantities from annual consumptions
        const updatedMaterialsUsed = entry.materialsUsed.map((productMaterial) => {
          // Locate this material in annual consumptions (by id or name)
          let annualMaterial: any = null;

          const matchMaterial = (annualMat: any) => {
            const byId = annualMat?.setup_item_id === productMaterial.materialId;
            const bySetupName = (annualMat?.setup_item_name || "").toLowerCase() === (productMaterial.materialName || "").toLowerCase();
            const byType = (annualMat?.materialType || "").toLowerCase() === (productMaterial.materialName || "").toLowerCase();
            const byName = (annualMat?.materialName || "").toLowerCase() === (productMaterial.materialName || "").toLowerCase();
            return byId || bySetupName || byType || byName;
          };

          if (calculationInputs.generalData.activityType === "extraction_and_transformation") {
            if (entry.productFlow === "extraction") {
              annualMaterial = (calculationInputs.consumptions.extraction?.materials as any)?.entries?.find(matchMaterial);
            } else if (entry.productFlow === "transformation") {
              annualMaterial = (calculationInputs.consumptions.transformation?.materials as any)?.entries?.find(matchMaterial);
            }
          } else {
            annualMaterial = formData?.materials?.entries?.find(matchMaterial);
          }

          if (annualMaterial && annualMaterial.quantity !== undefined) {
            // Determine allocation basis
            const isExtractionFlow = entry.productFlow === "extraction";

            // Consider only products in the same flow that use this material
            const productsUsingMaterial = (existingData.entries || []).filter((e) =>
              e.productFlow === entry.productFlow &&
              e.materialsUsed.some(
                (m) => m.materialId === productMaterial.materialId || m.materialName === productMaterial.materialName
              )
            );

            let totalForMaterial = 0;
            let currentProductValue = 0;

            if (isExtractionFlow) {
              // For extraction, use rd-based allocation (physical when rd <= 5, economic when rd > 5)
              const extractionAllocationType = getExtractionAllocationType(calculationInputs.generalData);
              if (extractionAllocationType === "physical") {
                totalForMaterial = productsUsingMaterial.reduce((sum, e) => sum + convertToTonnes(e).value, 0);
                currentProductValue = convertToTonnes(entry).value;
              } else {
                totalForMaterial = productsUsingMaterial.reduce((sum, e) => sum + (Number(e.productRevenue) || 0), 0);
                currentProductValue = Number(entry.productRevenue) || 0;
              }
            } else {
              // For transformation, always allocate materials by physical production
              totalForMaterial = productsUsingMaterial.reduce((sum, e) => sum + convertToTonnes(e).value, 0);
              currentProductValue = convertToTonnes(entry).value;
            }

            const materialSpecificRatio = totalForMaterial > 0 ? currentProductValue / totalForMaterial : 0;
            const allocatedQuantity = Number(annualMaterial.quantity) * materialSpecificRatio;

            return {
              ...productMaterial,
              quantity: allocatedQuantity,
              unit: annualMaterial.unit || productMaterial.unit,
            };
          }

          return productMaterial;
        });

        // Update materialsUsedHydric with economic (revenue-based) allocation
        const hydricSourceMaterials = entry.materialsUsedHydric || entry.materialsUsed;
        const updatedMaterialsUsedHydric = hydricSourceMaterials.map((productMaterial) => {
          let annualMaterial: any = null;

          const matchMaterial = (annualMat: any) => {
            const byId = annualMat?.setup_item_id === productMaterial.materialId;
            const bySetupName = (annualMat?.setup_item_name || "").toLowerCase() === (productMaterial.materialName || "").toLowerCase();
            const byType = (annualMat?.materialType || "").toLowerCase() === (productMaterial.materialName || "").toLowerCase();
            const byName = (annualMat?.materialName || "").toLowerCase() === (productMaterial.materialName || "").toLowerCase();
            return byId || bySetupName || byType || byName;
          };

          if (calculationInputs.generalData.activityType === "extraction_and_transformation") {
            if (entry.productFlow === "extraction") {
              annualMaterial = (calculationInputs.consumptions.extraction?.materials as any)?.entries?.find(matchMaterial);
            } else if (entry.productFlow === "transformation") {
              annualMaterial = (calculationInputs.consumptions.transformation?.materials as any)?.entries?.find(matchMaterial);
            }
          } else {
            annualMaterial = formData?.materials?.entries?.find(matchMaterial);
          }

          if (annualMaterial && annualMaterial.quantity !== undefined) {
            const productsUsingMaterial = (existingData.entries || []).filter((e) =>
              e.productFlow === entry.productFlow &&
              e.materialsUsed.some(
                (m) => m.materialId === productMaterial.materialId || m.materialName === productMaterial.materialName
              )
            );

            // Hydric always uses economic (revenue-based) allocation
            const totalForMaterial = productsUsingMaterial.reduce((sum, e) => sum + (Number(e.productRevenue) || 0), 0);
            const currentProductValue = Number(entry.productRevenue) || 0;
            const materialSpecificRatio = totalForMaterial > 0 ? currentProductValue / totalForMaterial : 0;
            const allocatedQuantity = Number(annualMaterial.quantity) * materialSpecificRatio;

            return {
              ...productMaterial,
              quantity: allocatedQuantity,
              unit: annualMaterial.unit || productMaterial.unit,
            };
          }

          return productMaterial;
        });

        // Create updated entry with new material quantities
        const updatedEntry = {
          ...entry,
          materialsUsed: updatedMaterialsUsed,
          materialsUsedHydric: updatedMaterialsUsedHydric,
        };

        // Recalculate ratios to avoid stale values when totals or entries change
        const productionInTonnes = convertToTonnes(updatedEntry).value;
        const isTransformationFlow = updatedEntry.productFlow === "transformation";
        const totalProductionTarget = isTransformationFlow
          ? Number(calculationInputs.generalData.prod_trsf) || 0
          : Number(calculationInputs.generalData.prod_ext) || 0;
        const totalRevenueTarget = isTransformationFlow
          ? Number(calculationInputs.generalData.fat_trsf) || 0
          : Number(calculationInputs.generalData.fat_ext) || 0;

        const ratioByQuantity = calculateRatioByQuantity(productionInTonnes, totalProductionTarget);
        const ratioByRevenue = calculateRatioByRevenue(Number(updatedEntry.productRevenue) || 0, totalRevenueTarget);

        const updatedEntryWithRatios = {
          ...updatedEntry,
          ratioByQuantity,
          ratioByRevenue,
          rendimento: calculationInputs.generalData.rend,
        };

        // Recalculate footprints for each product entry
        let newFootprints: any = {};

        if (updatedEntry.productFlow === "extraction") {
          newFootprints = calculateExtractionProductFootprints(
            updatedEntryWithRatios,
            existingData.entries,
            calculationInputs,
            materialDocs,
            stoneDocs
          );
        } else if (updatedEntry.productFlow === "transformation") {
          newFootprints = calculateTransformationProductFootprints(
            updatedEntryWithRatios,
            existingData.entries,
            calculationInputs,
            stoneDocs,
            transportDocs || [],
            materialDocs
          );
        }

        const currentFootprints = {
          carbonFootprintTotal: entry.carbonFootprintTotal,
          hydricFootprintTotal: entry.hydricFootprintTotal,
          carbonFootprintTransport: entry.carbonFootprintTransport,
          hydricFootprintTransport: entry.hydricFootprintTransport,
        };

        const newTotalFootprints = {
          carbonFootprintTotal:
            updatedEntry.productFlow === "extraction"
              ? newFootprints.pc_prod
              : newFootprints.carbonFootprintTotal,
          hydricFootprintTotal:
            updatedEntry.productFlow === "extraction"
              ? newFootprints.ph_prod
              : newFootprints.hydricFootprintTotal,
          carbonFootprintTransport: updatedEntry.productFlow === "extraction" ? 0 : newFootprints.pc_trans_total,
          hydricFootprintTransport: updatedEntry.productFlow === "extraction" ? 0 : newFootprints.ph_trans_total,
        };
        
        if (Math.abs((currentFootprints.carbonFootprintTotal || 0) - (newTotalFootprints.carbonFootprintTotal || 0)) > 0.001 ||
            Math.abs((currentFootprints.hydricFootprintTotal || 0) - (newTotalFootprints.hydricFootprintTotal || 0)) > 0.001 ||
            Math.abs((currentFootprints.carbonFootprintTransport || 0) - (newTotalFootprints.carbonFootprintTransport || 0)) > 0.001 ||
            Math.abs((currentFootprints.hydricFootprintTransport || 0) - (newTotalFootprints.hydricFootprintTransport || 0)) > 0.001) {
          hasChanges = true;
        }

        if (updatedEntry.productFlow === "extraction") {
          return {
            ...updatedEntryWithRatios,
            carbonFootprintMaterials: newFootprints.pc_ext_materiais,
            carbonFootprintElectricity: newFootprints.pc_ext_ecr,
            carbonFootprintElectricityOnly: newFootprints.pc_ele,
            carbonFootprintFuels: newFootprints.pc_comb,
            carbonFootprintResidue: newFootprints.pc_res,
            carbonFootprintTotal: newFootprints.pc_prod,
            hydricFootprintMaterials: newFootprints.ph_ext_materiais,
            hydricFootprintElectricity: newFootprints.ph_ext_ec,
            hydricFootprintWater: newFootprints.ph_ext_agua,
            hydricFootprintTotal: newFootprints.ph_prod,
            carbonFootprintTransport: 0,
            hydricFootprintTransport: 0,
          };
        } else if (updatedEntry.productFlow === "transformation") {
          return {
            ...updatedEntryWithRatios,
            carbonFootprintMaterials: newFootprints.pc_trsf_materiais,
            carbonFootprintElectricity: newFootprints.pc_trsf_ecr,
            carbonFootprintElectricityOnly: newFootprints.pc_ele,
            carbonFootprintFuels: newFootprints.pc_comb,
            carbonFootprintResidue: newFootprints.pc_res,
            carbonFootprintTotal: newFootprints.carbonFootprintTotal,
            hydricFootprintMaterials: newFootprints.ph_trsf_materiais,
            hydricFootprintElectricity: newFootprints.ph_trsf_ec,
            hydricFootprintWater: newFootprints.ph_trsf_agua,
            hydricFootprintTotal: newFootprints.hydricFootprintTotal,
            carbonFootprintTransport: newFootprints.pc_trans_total,
            hydricFootprintTransport: newFootprints.ph_trans_total,
            carbonFootprintTransformation: newFootprints.pc_trsf_total,
            hydricFootprintTransformation: newFootprints.ph_trsf_total,
          };
        }

        return entry;
      });

      if (hasChanges) {
        onDataChangeRef.current({
          ...existingData,
          entries: updatedEntries,
        });
      }
    }
  }, [
    calculationInputs,
    materialDocs,
    materialDocsLoading,
    stoneDocs,
    stoneDocsLoading,
    transportDocs,
    formData,
    existingData,
  ]);

  // Check if company data is still loading
  const isCompanyDataLoading = !company || !activityType;

  // Separate product entries for extraction and transformation
  const extractionEntries = existingData?.entries?.filter((e) => e.productFlow === "extraction") || [];
  const transformationEntries = existingData?.entries?.filter((e) => e.productFlow === "transformation") || [];

  // Separate validation for each
  const extractionValidation = validateProductTotals(extractionEntries, {
    prod_ext: calculationInputs.generalData.prod_ext || 0,
    fat_ext: calculationInputs.generalData.fat_ext || 0,
  });
  const transformationValidation = validateProductTotals(transformationEntries, {
    prod_ext: calculationInputs.generalData.prod_trsf || 0,
    fat_ext: calculationInputs.generalData.fat_trsf || 0,
  });

  // Only allow switching to transformation if at least one extraction product exists
  const canAccessTransformation = extractionEntries.length > 0;

  // Get the current entries and totals based on active submenu
  const currentProductionTarget =
    activeSubmenu === "extraction"
      ? calculationInputs.generalData.prod_ext || 0
      : calculationInputs.generalData.prod_trsf || 0;
  const currentRevenueTarget =
    activeSubmenu === "extraction"
      ? calculationInputs.generalData.fat_ext || 0
      : calculationInputs.generalData.fat_trsf || 0;

  const hasRequiredData = useMemo(() => {
    // If company data is still loading, return false
    if (isCompanyDataLoading) {
      return false;
    }

    const isExtractionCompany = activityType === "extraction";
    const isTransformationCompany = activityType === "transformation";
    const isMixedCompany = activityType === "extraction_and_transformation";
    
    let result = false;
    if (isExtractionCompany) {
      result = (
        (calculationInputs.generalData.prod_ext ?? 0) > 0 &&
        (calculationInputs.generalData.fat_ext ?? 0) > 0 &&
        !!calculationInputs.generalData.stoneTypeId
      );
    } else if (isTransformationCompany) {
      result = (calculationInputs.generalData.prod_trsf ?? 0) > 0 && (calculationInputs.generalData.fat_trsf ?? 0) > 0;
    } else if (isMixedCompany) {
      result = (
        (calculationInputs.generalData.prod_ext ?? 0) > 0 &&
        (calculationInputs.generalData.fat_ext ?? 0) > 0 &&
        !!calculationInputs.generalData.stoneTypeId &&
        (calculationInputs.generalData.prod_trsf ?? 0) > 0 &&
        (calculationInputs.generalData.fat_trsf ?? 0) > 0
      );
    }
    return result;
  }, [activityType, calculationInputs.generalData, isCompanyDataLoading]);

  // Check completion status
  useEffect(() => {
    // If company data is still loading, don't check completion
    if (isCompanyDataLoading) {
      return;
    }

    if (!hasRequiredData) {
      onIncompleteRef.current?.();
      return;
    }

    const isExtractionCompany = activityType === "extraction";
    const isTransformationCompany = activityType === "transformation";
    const isMixedCompany = activityType === "extraction_and_transformation";

    if (isExtractionCompany) {
      if (extractionValidation.isProductionValid && extractionValidation.isRevenueValid) {
        onCompleteRef.current?.();
      } else {
        onIncompleteRef.current?.();
      }
    } else if (isTransformationCompany) {
      if (transformationValidation.isProductionValid && transformationValidation.isRevenueValid) {
        onCompleteRef.current?.();
      } else {
        onIncompleteRef.current?.();
      }
    } else if (isMixedCompany) {
      const isExtractionPhaseComplete = extractionValidation.isProductionValid && extractionValidation.isRevenueValid;
      const isTransformationPhaseCompleteOrEmpty =
        (transformationValidation.isProductionValid && transformationValidation.isRevenueValid) ||
        transformationEntries.length === 0;

      if (isExtractionPhaseComplete && isTransformationPhaseCompleteOrEmpty) {
        onCompleteRef.current?.();
      } else {
        onIncompleteRef.current?.();
      }
    }
  }, [
    activityType,
    hasRequiredData,
    extractionValidation.isProductionValid,
    extractionValidation.isRevenueValid,
    transformationValidation.isProductionValid,
    transformationValidation.isRevenueValid,
    transformationEntries.length,
    isCompanyDataLoading,
  ]);

  // Recalculate validation when general data changes
  useEffect(() => {
    if (isCompanyDataLoading || !existingData?.entries?.length) {
      return;
    }

    // Recalculate validation with current general data
    const updatedValidation = calculateProductValidation(
      existingData,
      calculationInputs.generalData,
      activityType
    );

    // Only update if validation has changed
    if (
      updatedValidation.isProductionValid !== existingData.isProductionValid ||
      updatedValidation.isRevenueValid !== existingData.isRevenueValid
    ) {
      onDataChangeRef.current(updatedValidation);
    }
  }, [
    calculationInputs.generalData.prod_ext,
    calculationInputs.generalData.fat_ext,
    calculationInputs.generalData.prod_trsf,
    calculationInputs.generalData.fat_trsf,
    existingData?.entries,
    activityType,
    isCompanyDataLoading,
  ]);

  const openModal = (entry?: ProductEntry, idx?: number) => {
    if (entry && idx !== undefined && existingData?.entries) {
      // Find the correct global index in existingData.entries by matching entry ID
      const globalIndex = existingData.entries.findIndex((e) => e.id === entry.id);
      setEditingIndex(globalIndex !== -1 ? globalIndex : null);
      setFormEntry(JSON.parse(JSON.stringify(entry)));
    } else if (entry) {
      setEditingIndex(null);
      setFormEntry(JSON.parse(JSON.stringify(entry)));
    } else {
      setEditingIndex(null);
      setFormEntry(createEmptyEntry(activeSubmenu));
    }
    setModalOpen(true);
  };

  // Sync formEntry with existingData.entries when editing and modal is open
  useEffect(() => {
    if (modalOpen && editingIndex !== null && existingData?.entries) {
      setFormEntry(JSON.parse(JSON.stringify(existingData.entries[editingIndex])));
    }
  }, [modalOpen, editingIndex, existingData?.entries]);

  // Reset formEntry on modal close
  const closeModal = () => {
    setModalOpen(false);
    setEditingIndex(null);
    setFormEntry(createEmptyEntry(activeSubmenu));
  };

  const handleSave = (calculatedEntry: ProductEntry) => {
    // Ensure productFlow is set for transformation-only companies
    let updatedEntry = { ...calculatedEntry };
    if (activityType === "transformation") {
      updatedEntry.productFlow = "transformation";
    }

    // Synchronize thickness fields for data consistency
    // Ensure both functionalUnit.thickness and espessura contain the same value (in cm)
    if (updatedEntry.functionalUnit?.thickness !== undefined) {
      updatedEntry.espessura = updatedEntry.functionalUnit.thickness;
    } else if (updatedEntry.espessura !== undefined) {
      // If only espessura exists, sync it to functionalUnit.thickness
      if (!updatedEntry.functionalUnit) {
        updatedEntry.functionalUnit = { quantity: 1, unit: "m2" };
      }
      updatedEntry.functionalUnit.thickness = updatedEntry.espessura;
    }
    let updatedEntries = existingData?.entries ? [...existingData.entries] : [];
    if (editingIndex !== null) {
      updatedEntries[editingIndex] = updatedEntry;
    } else {
      updatedEntries.push(updatedEntry);
    }

    // Recalculate ratios for ALL entries against current targets to avoid stale ratios
    updatedEntries = updatedEntries.map((entry) => {
      const productionInTonnes = convertToTonnes(entry).value;
      const isTransformationFlow = entry.productFlow === "transformation";
      const totalProductionTarget = isTransformationFlow
        ? Number(calculationInputs.generalData.prod_trsf) || 0
        : Number(calculationInputs.generalData.prod_ext) || 0;
      const totalRevenueTarget = isTransformationFlow
        ? Number(calculationInputs.generalData.fat_trsf) || 0
        : Number(calculationInputs.generalData.fat_ext) || 0;
      return {
        ...entry,
        ratioByQuantity: calculateRatioByQuantity(productionInTonnes, totalProductionTarget),
        ratioByRevenue: calculateRatioByRevenue(Number(entry.productRevenue) || 0, totalRevenueTarget),
      };
    });

    // Calculate totals
    const totalProductionResult = updatedEntries.reduce(
      (acc, entry) => {
        const result = convertToTonnes(entry);
        acc.value += result?.value || 0;
        if (result?.usedDefaultThickness) acc.usedDefaultThickness = true;
        if (result?.usedDefaultDensity) acc.usedDefaultDensity = true;
        return acc;
      },
      { value: 0, usedDefaultThickness: false, usedDefaultDensity: false }
    );

    const totalProduction = totalProductionResult?.value || 0;
    const totalRevenue = updatedEntries.reduce((sum, entry) => sum + (Number(entry.productRevenue) || 0), 0);

    // Determine validation based on company type
    const isExtractionCompany = activityType === "extraction";
    const isTransformationCompany = activityType === "transformation";
    const isMixedCompany = activityType === "extraction_and_transformation";

    let isProductionValid = false;
    let isRevenueValid = false;

    if (isExtractionCompany) {
      const validation = validateProductTotals(updatedEntries, {
        prod_ext: calculationInputs.generalData.prod_ext || 0,
        fat_ext: calculationInputs.generalData.fat_ext || 0,
      });
      isProductionValid = validation.isProductionValid;
      isRevenueValid = validation.isRevenueValid;
    } else if (isTransformationCompany) {
      const validation = validateProductTotals(updatedEntries, {
        prod_ext: calculationInputs.generalData.prod_trsf || 0,
        fat_ext: calculationInputs.generalData.fat_trsf || 0,
      });
      isProductionValid = validation.isProductionValid;
      isRevenueValid = validation.isRevenueValid;
    } else if (isMixedCompany) {
      const extractionEntries = updatedEntries.filter((e) => e.productFlow === "extraction");
      const transformationEntries = updatedEntries.filter((e) => e.productFlow === "transformation");

      const extractionValidation = validateProductTotals(extractionEntries, {
        prod_ext: calculationInputs.generalData.prod_ext || 0,
        fat_ext: calculationInputs.generalData.fat_ext || 0,
      });
      const transformationValidation = validateProductTotals(transformationEntries, {
        prod_ext: calculationInputs.generalData.prod_trsf || 0,
        fat_ext: calculationInputs.generalData.fat_trsf || 0,
      });

      isProductionValid = extractionValidation.isProductionValid && transformationValidation.isProductionValid;
      isRevenueValid = extractionValidation.isRevenueValid && transformationValidation.isRevenueValid;
    }

    const payload: ProductPayload = {
      entries: updatedEntries,
      totalAnnualProduction: totalProduction,
      totalRevenue,
      isProductionValid,
      isRevenueValid,
      deletedProductIds: existingData?.deletedProductIds || [],
    };

    onDataChangeRef.current(payload);
    closeModal();
  };

  const handleDeleteProduct = (index: number) => {
    // Instead of relying on activeSubmenu state which might be out of sync,
    // we'll find the entry by looking at all currently displayed entries
    // and then find it in the full entries array by ID

    let currentEntries: ProductEntry[] = [];

    if (activityType === "extraction_and_transformation") {
      currentEntries = activeSubmenu === "extraction" ? extractionEntries : transformationEntries;
    } else if (activityType === "extraction") {
      currentEntries = extractionEntries;
    } else if (activityType === "transformation") {
      currentEntries = transformationEntries;
    }

    const entryToDelete = currentEntries[index];

    if (!entryToDelete) {
      console.error("Could not find entry to delete at index", index);
      return;
    }

    // Find the actual global index in the full entries array by matching the ID
    const globalIndex = existingData?.entries?.findIndex((e) => e.id === entryToDelete.id) ?? -1;

    if (globalIndex === -1) {
      console.error("Could not find entry in global entries array");
      return;
    }

    setProductToDelete({ entry: entryToDelete, index: globalIndex });
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!productToDelete) return;

    setIsDeleting(true);

    try {
      // Find the global index in the full entries array by matching ID
      const updatedEntries = existingData?.entries ? [...existingData.entries] : [];
      const globalIndex = updatedEntries.findIndex((e) => e.id === productToDelete.entry.id);

      if (globalIndex === -1) return;

      updatedEntries.splice(globalIndex, 1);

      // Calculate totals
      const totalProductionResult = updatedEntries.reduce(
        (acc, entry) => {
          const result = convertToTonnes(entry);
          acc.value += result?.value || 0;
          if (result?.usedDefaultThickness) acc.usedDefaultThickness = true;
          if (result?.usedDefaultDensity) acc.usedDefaultDensity = true;
          return acc;
        },
        { value: 0, usedDefaultThickness: false, usedDefaultDensity: false }
      );

      const totalProduction = totalProductionResult?.value || 0;
      const totalRevenue = updatedEntries.reduce((sum, entry) => sum + (Number(entry.productRevenue) || 0), 0);

      // Determine validation based on company type
      const isExtractionCompany = activityType === "extraction";
      const isTransformationCompany = activityType === "transformation";
      const isMixedCompany = activityType === "extraction_and_transformation";

      let isProductionValid = false;
      let isRevenueValid = false;

      if (isExtractionCompany) {
        const validation = validateProductTotals(updatedEntries, {
          prod_ext: calculationInputs.generalData.prod_ext || 0,
          fat_ext: calculationInputs.generalData.fat_ext || 0,
        });
        isProductionValid = validation.isProductionValid;
        isRevenueValid = validation.isRevenueValid;
      } else if (isTransformationCompany) {
        const validation = validateProductTotals(updatedEntries, {
          prod_ext: calculationInputs.generalData.prod_trsf || 0,
          fat_ext: calculationInputs.generalData.fat_trsf || 0,
        });
        isProductionValid = validation.isProductionValid;
        isRevenueValid = validation.isRevenueValid;
      } else if (isMixedCompany) {
        const extractionEntries = updatedEntries.filter((e) => e.productFlow === "extraction");
        const transformationEntries = updatedEntries.filter((e) => e.productFlow === "transformation");

        const extractionValidation = validateProductTotals(extractionEntries, {
          prod_ext: calculationInputs.generalData.prod_ext || 0,
          fat_ext: calculationInputs.generalData.fat_ext || 0,
        });
        const transformationValidation = validateProductTotals(transformationEntries, {
          prod_ext: calculationInputs.generalData.prod_trsf || 0,
          fat_ext: calculationInputs.generalData.fat_trsf || 0,
        });

        isProductionValid = extractionValidation.isProductionValid && transformationValidation.isProductionValid;
        isRevenueValid = extractionValidation.isRevenueValid && transformationValidation.isRevenueValid;
      }

      // Track deleted product IDs
      const deletedProductIds = existingData?.deletedProductIds || [];
      if (!deletedProductIds.includes(productToDelete.entry.id)) {
        deletedProductIds.push(productToDelete.entry.id);
      }

      const payload: ProductPayload = {
        entries: updatedEntries,
        totalAnnualProduction: totalProduction,
        totalRevenue,
        isProductionValid,
        isRevenueValid,
        deletedProductIds,
      };

      onDataChangeRef.current(payload);
    } catch (error) {
      console.error("Error deleting product:", error);
    } finally {
      setIsDeleting(false);
      setConfirmDialogOpen(false);
      setProductToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDialogOpen(false);
    setProductToDelete(null);
  };

  const [activeStep, setActiveStep] = useState(0);
  const steps = [
    {
      id: "extraction",
      title: t("products.extractionProduct"),
      isCompleted: extractionValidation.isProductionValid && extractionValidation.isRevenueValid,
    },
    {
      id: "transformation",
      title: t("products.transformationProduct"),
      isCompleted: transformationValidation.isProductionValid && transformationValidation.isRevenueValid,
    },
  ];

  const renderSingleActivityForm = () => {
    return (
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl text-teal-700">
            {editingIndex !== null ? t("products.editProduct") : t("products.addProduct")}
          </DialogTitle>
        </DialogHeader>
        <ProductEntryModal
          open={modalOpen}
          onSave={handleSave}
          onClose={closeModal}
          formEntry={formEntry}
          editing={editingIndex !== null}
          generalStoneTypeId={calculationInputs.generalData.stoneTypeId}
          activityType={activityType}
          calculationInputs={calculationInputs}
          allEntries={existingData?.entries || []}
          totalAnnualProduction={
            activityType === "transformation"
              ? calculationInputs.generalData.prod_trsf || 0
              : calculationInputs.generalData.prod_ext || 0
          }
          totalRevenue={
            activityType === "transformation"
              ? calculationInputs.generalData.fat_trsf || 0
              : calculationInputs.generalData.fat_ext || 0
          }
          formData={formData}
          extractionProducts={extractionEntries}
          currentCompanyId={claims?.companyId}
        />
      </DialogContent>
    );
  };

  const renderMixedActivityForm = () => {
    return (
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl text-teal-700">
            {editingIndex !== null ? t("products.editProduct") : t("products.addProduct")}
          </DialogTitle>
        </DialogHeader>
        <MixedActivityProductForm
          onSave={handleSave}
          onClose={closeModal}
          formEntry={formEntry}
          editing={editingIndex !== null}
          generalStoneTypeId={calculationInputs.generalData.stoneTypeId}
          calculationInputs={calculationInputs}
          allEntries={existingData?.entries || []}
          totalAnnualProduction={currentProductionTarget}
          totalRevenue={currentRevenueTarget}
          formData={formData}
          extractionProducts={extractionEntries}
          currentCompanyId={claims?.companyId}
        />
      </DialogContent>
    );
  };

  // Main display logic
  // Show loading state if company data is not loaded yet
  if (isCompanyDataLoading) {
    return (
      <div className="w-full max-w-5xl mx-auto">
        <ConfidentialityDisclaimer />
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading company data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!hasRequiredData) {
    const currentProductionField =
      activityType === "transformation"
        ? calculationInputs.generalData.prod_trsf || 0
        : calculationInputs.generalData.prod_ext || 0;
    const currentRevenueField =
      activityType === "transformation"
        ? calculationInputs.generalData.fat_trsf || 0
        : calculationInputs.generalData.fat_ext || 0;

    return (
      <div className="w-full max-w-5xl mx-auto">
        <ConfidentialityDisclaimer />
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-medium text-gray-700">{t("products.title")}</h2>
            <div className="flex items-center text-teal-600 bg-teal-50 px-2.5 py-1 rounded-full border border-teal-200 text-xs font-medium">
              <CalendarIcon className="w-3.5 h-3.5 mr-1.5" />
              <span>{year}</span>
            </div>
          </div>

          <div className="space-y-8">
            {/* Informational Header */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-6 w-6 text-amber-600 mt-0.5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-amber-800 mb-2">{t("products.generalDataRequired")}</h3>
                  <p className="text-amber-700 text-sm leading-relaxed mb-4">
                    {t("products.generalDataRequiredDescription")}
                  </p>
                  <div className="bg-white rounded-lg p-4 border border-amber-200">
                    <h4 className="font-medium text-amber-800 mb-3">{t("products.requiredDataItems")}:</h4>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div
                          className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                            currentProductionField > 0 ? "bg-green-400" : "bg-amber-400"
                          }`}
                        ></div>
                        <div>
                          <p className="font-medium text-gray-700">
                            {activityType === "transformation"
                              ? t("footprints.generalData.totalTransformationProduction")
                              : t("footprints.generalData.totalExtractionProduction")}
                          </p>
                          <p className="text-sm text-gray-600">
                            {activityType === "transformation"
                              ? t("products.transformationProductionDescription")
                              : t("products.extractionProductionDescription")}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div
                          className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                            currentRevenueField > 0 ? "bg-green-400" : "bg-amber-400"
                          }`}
                        ></div>
                        <div>
                          <p className="font-medium text-gray-700">
                            {activityType === "transformation"
                              ? t("footprints.generalData.totalTransformationRevenue")
                              : t("footprints.generalData.totalExtractionRevenue")}
                          </p>
                          <p className="text-sm text-gray-600">
                            {activityType === "transformation"
                              ? t("products.transformationRevenueDescription")
                              : t("products.extractionRevenueDescription")}
                          </p>
                        </div>
                      </div>
                      {/* Only show stone type requirement for extraction and mixed companies */}
                      {(activityType === "extraction" || activityType === "extraction_and_transformation") && (
                        <div className="flex items-start gap-3">
                          <div
                            className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                              calculationInputs.generalData.stoneTypeId ? "bg-green-400" : "bg-amber-400"
                            }`}
                          ></div>
                          <div>
                            <p className="font-medium text-gray-700">{t("footprints.generalData.stoneType")}</p>
                            <p className="text-sm text-gray-600">{t("products.stoneTypeDescription")}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* What Products Are */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <Package className="h-6 w-6 text-blue-600 mt-0.5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-blue-800 mb-2">
                    {activityType === "transformation"
                      ? t("products.aboutTransformationProductsTitle")
                      : t("products.aboutProductsTitle")}
                  </h3>
                  <p className="text-blue-700 text-sm leading-relaxed mb-4">
                    {activityType === "transformation"
                      ? t("products.aboutTransformationProductsDescription")
                      : t("products.aboutProductsDescription")}
                  </p>
                  <div className="bg-white rounded-lg p-4 border border-blue-200">
                    <h4 className="font-medium text-blue-800 mb-3">{t("products.productExamples")}:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {activityType === "transformation" ? (
                        <>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0"></div>
                            <span className="text-sm text-gray-700">{t("products.examplePavement")}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0"></div>
                            <span className="text-sm text-gray-700">{t("products.exampleCladding")}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0"></div>
                            <span className="text-sm text-gray-700">{t("products.exampleSheet")}</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0"></div>
                            <span className="text-sm text-gray-700">{t("products.exampleBlocks")}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0"></div>
                            <span className="text-sm text-gray-700">{t("products.exampleSlabs")}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0"></div>
                            <span className="text-sm text-gray-700">{t("products.exampleTiles")}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Next Steps Information */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-800">{t("products.nextStepsTitle")}</p>
                  <p className="text-xs text-green-700 mt-1">{t("products.nextStepsDescription")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Mixed activity company (extraction_and_transformation)
  if (activityType === "extraction_and_transformation") {
    return (
      <div className="w-full max-w-5xl mx-auto">
        <ConfidentialityDisclaimer />
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
          <Stepper
            steps={steps.map((step, idx) => ({
              ...step,
              isEnabled: idx === 0 || canAccessTransformation,
            }))}
            activeStep={activeStep}
            onStepChange={(idx) => {
              if (idx === 0 || canAccessTransformation) {
                setActiveStep(idx);
                setActiveSubmenu(idx === 0 ? "extraction" : "transformation");
              }
            }}
            orientation="horizontal"
            variant="default"
            size="md"
          >
            {/* Extraction Phase */}
            {activeSubmenu === "extraction" && (
              <>
                <ProductSummary
                  validation={extractionValidation}
                  currentProductionTarget={calculationInputs.generalData.prod_ext || 0}
                  currentRevenueTarget={calculationInputs.generalData.fat_ext || 0}
                  totalProduction={extractionEntries.reduce(
                    (acc, entry) => acc + (convertToTonnes(entry).value || 0),
                    0
                  )}
                  usedDefaultDensity={extractionEntries.some((entry) => convertToTonnes(entry).usedDefaultDensity)}
                  calculatedEntries={extractionEntries}
                  productFlow="extraction"
                  t={t}
                />
                {extractionEntries.length === 0 ? (
                  <Card className="border-2 border-dashed border-gray-300 bg-gray-50">
                    <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                      <Package className="h-12 w-12 text-gray-400 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">{t("products.noProducts")}</h3>
                      <p className="text-gray-500 mb-4 max-w-md">{t("products.noProductsDescription")}</p>
                      <Button onClick={() => openModal()} className="bg-teal-600 hover:bg-teal-700 text-white">
                        <Plus className="h-4 w-4 mr-2" />
                        {t("products.addFirstProduct")}
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    <div className="flex justify-end mb-4">
                      <Button onClick={() => openModal()} className="bg-teal-600 hover:bg-teal-700 text-white">
                        <Plus className="h-4 w-4 mr-2" />
                        {t("products.addProduct")}
                      </Button>
                    </div>
                    <ProductTable
                      entries={extractionEntries}
                      onEdit={openModal}
                      onDelete={handleDeleteProduct}
                      materialDocs={materialDocs}
                    />
                  </>
                )}
                {/* Navigation button to transformation products */}
                {extractionEntries.length > 0 && (
                    <div className="mt-6 flex justify-end">
                      <Button
                        onClick={() => {
                          setActiveStep(1);
                          setActiveSubmenu("transformation");
                        }}
                        className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-3 text-base font-medium"
                        size="lg"
                      >
                        {t("products.continueToTransformationProducts")}
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </div>
                  )}
                {hasUnsavedChanges && onSaveDraft && (
                  <div className="my-4 flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <AlertCircleIcon className="h-4 w-4" />
                        <span>{t("footprints.consumption.unsavedChanges")}</span>
                      </div>
                      {lastSaved && (
                        <div className="text-xs text-gray-500">
                          {t("footprints.consumption.lastSaved")}:{" "}
                          {(() => {
                            // Convert Firestore timestamp to Date if needed
                            let date = lastSaved;
                            if (lastSaved && typeof lastSaved === "object" && "seconds" in lastSaved) {
                              date = new Date((lastSaved as { seconds: number }).seconds * 1000);
                            }
                            return date instanceof Date
                              ? date.toLocaleString("pt-PT")
                              : t("footprints.consumption.invalidDate");
                          })()}
                        </div>
                      )}
                    </div>
                    <Button
                      onClick={onSaveDraft}
                      disabled={isSavingDraft}
                      variant="outline"
                      size="sm"
                      className="border-teal-200 text-teal-600 hover:bg-teal-50"
                    >
                      {isSavingDraft ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          {t("footprints.consumption.saving")}
                        </>
                      ) : (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          {t("footprints.consumption.saveDraft")}
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </>
            )}
            {/* Transformation Phase */}
            {activeSubmenu === "transformation" && canAccessTransformation ? (
              <>
                <ProductSummary
                  validation={transformationValidation}
                  currentProductionTarget={calculationInputs.generalData.prod_trsf || 0}
                  currentRevenueTarget={calculationInputs.generalData.fat_trsf || 0}
                  totalProduction={transformationEntries.reduce(
                    (acc, entry) => acc + (convertToTonnes(entry).value || 0),
                    0
                  )}
                  usedDefaultDensity={transformationEntries.some((entry) => convertToTonnes(entry).usedDefaultDensity)}
                  calculatedEntries={transformationEntries}
                  productFlow="transformation"
                  t={t}
                />
                {transformationEntries.length === 0 ? (
                  <Card className="border-2 border-dashed border-gray-300 bg-gray-50">
                    <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                      <Package className="h-12 w-12 text-gray-400 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">{t("products.noProducts")}</h3>
                      <p className="text-gray-500 mb-4 max-w-md">{t("products.noProductsDescription")}</p>
                      <Button onClick={() => openModal()} className="bg-teal-600 hover:bg-teal-700 text-white">
                        <Plus className="h-4 w-4 mr-2" />
                        {t("products.addFirstProduct")}
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    <div className="flex justify-end mb-4">
                      <Button onClick={() => openModal()} className="bg-teal-600 hover:bg-teal-700 text-white">
                        <Plus className="h-4 w-4 mr-2" />
                        {t("products.addProduct")}
                      </Button>
                    </div>
                    <ProductTable
                      entries={transformationEntries}
                      onEdit={openModal}
                      onDelete={handleDeleteProduct}
                      materialDocs={materialDocs}
                    />
                  </>
                )}
                {/* Navigation button to extraction products */}
                <div className="mt-6 flex justify-start">
                  <Button
                    onClick={() => {
                      setActiveStep(0);
                      setActiveSubmenu("extraction");
                    }}
                    variant="outline"
                    className="border-teal-200 text-teal-600 hover:bg-teal-50 px-8 py-3 text-base font-medium"
                    size="lg"
                  >
                    <ArrowLeft className="mr-2 h-5 w-5" />
                    {t("products.backToExtractionProducts")}
                  </Button>
                </div>
                {hasUnsavedChanges && onSaveDraft && (
                  <div className="my-4 flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <AlertCircleIcon className="h-4 w-4" />
                        <span>{t("footprints.consumption.unsavedChanges")}</span>
                      </div>
                      {lastSaved && (
                        <div className="text-xs text-gray-500">
                          {t("footprints.consumption.lastSaved")}:{" "}
                          {(() => {
                            // Convert Firestore timestamp to Date if needed
                            let date = lastSaved;
                            if (lastSaved && typeof lastSaved === "object" && "seconds" in lastSaved) {
                              date = new Date((lastSaved as { seconds: number }).seconds * 1000);
                            }
                            return date instanceof Date
                              ? date.toLocaleString("pt-PT")
                              : t("footprints.consumption.invalidDate");
                          })()}
                        </div>
                      )}
                    </div>
                    <Button
                      onClick={onSaveDraft}
                      disabled={isSavingDraft}
                      variant="outline"
                      size="sm"
                      className="border-teal-200 text-teal-600 hover:bg-teal-50"
                    >
                      {isSavingDraft ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          {t("footprints.consumption.saving")}
                        </>
                      ) : (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          {t("footprints.consumption.saveDraft")}
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </>
            ) : activeSubmenu === "transformation" && !canAccessTransformation ? (
              <div className="mt-8 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                <p>{t("products.completeExtractionFirst")}</p>
              </div>
            ) : null}
          </Stepper>
        </div>

        {/* Centralized Dialog for Mixed Activity */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          {activityType === "extraction_and_transformation" ? renderMixedActivityForm() : renderSingleActivityForm()}
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={confirmDialogOpen}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title={t("products.confirmDelete.title")}
          description={t("products.confirmDelete.description")}
          confirmText={isDeleting ? t("products.confirmDelete.actionLoading") : t("products.confirmDelete.action")}
          cancelText={t("products.cancel")}
          variant="destructive"
          isLoading={isDeleting}
        />
      </div>
    );
  }

  // Extraction-only company
  if (activityType === "extraction") {
    return (
      <div className="w-full max-w-5xl mx-auto">
        <ConfidentialityDisclaimer />
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-medium text-gray-700">{t("products.title")}</h2>
            <div className="flex items-center text-teal-600 bg-teal-50 px-2.5 py-1 rounded-full border border-teal-200 text-xs font-medium">
              <CalendarIcon className="w-3.5 h-3.5 mr-1.5" />
              <span>{year}</span>
            </div>
          </div>
          <div className="mb-6">
            <p className="text-sm text-gray-600">{t("products.description")}</p>
          </div>
          <ProductSummary
            validation={extractionValidation}
            currentProductionTarget={calculationInputs.generalData.prod_ext || 0}
            currentRevenueTarget={calculationInputs.generalData.fat_ext || 0}
            totalProduction={extractionEntries.reduce((acc, entry) => acc + (convertToTonnes(entry).value || 0), 0)}
            usedDefaultDensity={extractionEntries.some((entry) => convertToTonnes(entry).usedDefaultDensity)}
            calculatedEntries={extractionEntries}
            productFlow="extraction"
            t={t}
          />
          {extractionEntries.length === 0 ? (
            <Card className="border-2 border-dashed border-gray-300 bg-gray-50">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <Package className="h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t("products.noProducts")}</h3>
                <p className="text-gray-500 mb-4 max-w-md">{t("products.noProductsDescription")}</p>
                <Button onClick={() => openModal()} className="bg-teal-600 hover:bg-teal-700 text-white">
                  <Plus className="h-4 w-4 mr-2" />
                  {t("products.addFirstProduct")}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="flex justify-end mb-4">
                <Button onClick={() => openModal()} className="bg-teal-600 hover:bg-teal-700 text-white">
                  <Plus className="h-4 w-4 mr-2" />
                  {t("products.addProduct")}
                </Button>
              </div>
              <ProductTable
                entries={extractionEntries}
                onEdit={openModal}
                onDelete={handleDeleteProduct}
                materialDocs={materialDocs}
              />
            </>
          )}
          <Dialog open={modalOpen} onOpenChange={setModalOpen}>
            <DialogContent className="max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl text-teal-700">
                  {editingIndex !== null ? t("products.editProduct") : t("products.addProduct")}
                </DialogTitle>
              </DialogHeader>
              <ProductEntryModal
                open={modalOpen}
                onSave={handleSave}
                onClose={closeModal}
                formEntry={formEntry}
                editing={editingIndex !== null}
                generalStoneTypeId={calculationInputs.generalData.stoneTypeId}
                activityType={activityType}
                calculationInputs={calculationInputs}
                allEntries={extractionEntries}
                totalAnnualProduction={calculationInputs.generalData.prod_ext || 0}
                totalRevenue={calculationInputs.generalData.fat_ext || 0}
                formData={formData}
                extractionProducts={extractionEntries}
              />
            </DialogContent>
          </Dialog>
          {hasUnsavedChanges && onSaveDraft && (
            <div className="my-4 flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <AlertCircleIcon className="h-4 w-4" />
                  <span>{t("footprints.consumption.unsavedChanges")}</span>
                </div>
                {lastSaved && (
                  <div className="text-xs text-gray-500">
                    {t("footprints.consumption.lastSaved")}:{" "}
                    {(() => {
                      // Convert Firestore timestamp to Date if needed
                      let date = lastSaved;
                      if (lastSaved && typeof lastSaved === "object" && "seconds" in lastSaved) {
                        date = new Date((lastSaved as { seconds: number }).seconds * 1000);
                      }
                      return date instanceof Date
                        ? date.toLocaleString("pt-PT")
                        : t("footprints.consumption.invalidDate");
                    })()}
                  </div>
                )}
              </div>
              <Button
                onClick={onSaveDraft}
                disabled={isSavingDraft}
                variant="outline"
                size="sm"
                className="border-teal-200 text-teal-600 hover:bg-teal-50"
              >
                {isSavingDraft ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    {t("footprints.consumption.saving")}
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    {t("footprints.consumption.saveDraft")}
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Delete Confirmation Dialog */}
          <ConfirmationDialog
            isOpen={confirmDialogOpen}
            onClose={handleCancelDelete}
            onConfirm={handleConfirmDelete}
            title={t("products.confirmDelete.title")}
            description={t("products.confirmDelete.description")}
            confirmText={isDeleting ? t("products.confirmDelete.actionLoading") : t("products.confirmDelete.action")}
            cancelText={t("products.cancel")}
            variant="destructive"
            isLoading={isDeleting}
          />
        </div>
      </div>
    );
  }

  // Transformation-only company
  if (activityType === "transformation") {
    return (
      <div className="w-full max-w-5xl mx-auto">
        <ConfidentialityDisclaimer />
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-medium text-gray-700">{t("products.title")}</h2>
            <div className="flex items-center text-teal-600 bg-teal-50 px-2.5 py-1 rounded-full border border-teal-200 text-xs font-medium">
              <CalendarIcon className="w-3.5 h-3.5 mr-1.5" />
              <span>{year}</span>
            </div>
          </div>
          <div className="mb-6">
            <p className="text-sm text-gray-600">{t("products.description")}</p>
          </div>
          <ProductSummary
            validation={transformationValidation}
            currentProductionTarget={calculationInputs.generalData.prod_trsf || 0}
            currentRevenueTarget={calculationInputs.generalData.fat_trsf || 0}
            totalProduction={transformationEntries.reduce((acc, entry) => acc + (convertToTonnes(entry).value || 0), 0)}
            usedDefaultDensity={transformationEntries.some((entry) => convertToTonnes(entry).usedDefaultDensity)}
            calculatedEntries={transformationEntries}
            productFlow="transformation"
            t={t}
          />
          {transformationEntries.length === 0 ? (
            <Card className="border-2 border-dashed border-gray-300 bg-gray-50">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <Package className="h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t("products.noProducts")}</h3>
                <p className="text-gray-500 mb-4 max-w-md">{t("products.noProductsDescription")}</p>
                <Button onClick={() => openModal()} className="bg-teal-600 hover:bg-teal-700 text-white">
                  <Plus className="h-4 w-4 mr-2" />
                  {t("products.addFirstProduct")}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="flex justify-end mb-4">
                <Button onClick={() => openModal()} className="bg-teal-600 hover:bg-teal-700 text-white">
                  <Plus className="h-4 w-4 mr-2" />
                  {t("products.addProduct")}
                </Button>
              </div>
              <ProductTable
                entries={transformationEntries}
                onEdit={openModal}
                onDelete={handleDeleteProduct}
                materialDocs={materialDocs}
              />
            </>
          )}
          <Dialog open={modalOpen} onOpenChange={setModalOpen}>
            <DialogContent className="max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl text-teal-700">
                  {editingIndex !== null ? t("products.editProduct") : t("products.addProduct")}
                </DialogTitle>
              </DialogHeader>
              <ProductEntryModal
                open={modalOpen}
                onSave={handleSave}
                onClose={closeModal}
                formEntry={formEntry}
                editing={editingIndex !== null}
                generalStoneTypeId={calculationInputs.generalData.stoneTypeId}
                activityType={activityType}
                calculationInputs={calculationInputs}
                allEntries={transformationEntries}
                totalAnnualProduction={calculationInputs.generalData.prod_trsf || 0}
                totalRevenue={calculationInputs.generalData.fat_trsf || 0}
                formData={formData}
                extractionProducts={extractionEntries}
              />
            </DialogContent>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <ConfirmationDialog
            isOpen={confirmDialogOpen}
            onClose={handleCancelDelete}
            onConfirm={handleConfirmDelete}
            title={t("products.confirmDelete.title")}
            description={t("products.confirmDelete.description")}
            confirmText={isDeleting ? t("products.confirmDelete.actionLoading") : t("products.confirmDelete.action")}
            cancelText={t("products.cancel")}
            variant="destructive"
            isLoading={isDeleting}
          />

          {hasUnsavedChanges && onSaveDraft && (
            <div className="my-4 flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <AlertCircleIcon className="h-4 w-4" />
                  <span>{t("footprints.consumption.unsavedChanges")}</span>
                </div>
                {lastSaved && (
                  <div className="text-xs text-gray-500">
                    {t("footprints.consumption.lastSaved")}:{" "}
                    {(() => {
                      // Convert Firestore timestamp to Date if needed
                      let date = lastSaved;
                      if (lastSaved && typeof lastSaved === "object" && "seconds" in lastSaved) {
                        date = new Date((lastSaved as { seconds: number }).seconds * 1000);
                      }
                      return date instanceof Date
                        ? date.toLocaleString("pt-PT")
                        : t("footprints.consumption.invalidDate");
                    })()}
                  </div>
                )}
              </div>
              <Button
                onClick={onSaveDraft}
                disabled={isSavingDraft}
                variant="outline"
                size="sm"
                className="border-teal-200 text-teal-600 hover:bg-teal-50"
              >
                {isSavingDraft ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    {t("footprints.consumption.saving")}
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    {t("footprints.consumption.saveDraft")}
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Fallback
  return null;
};
